// medplum-consult-enrichment.js — ONLINE-PRIMARY longitudinal enrichment loader.
// The browser fetches the patient-wide clinical resources the consult's deep
// panels need — RLS-scoped to the logged-in doctor by their own token — and runs
// the PURE engines in shape/consult-enrichment.js to produce the markers /
// vitalSeries / insightsBar / insightsRanked / episode DTO the consult screens
// read. This is the online-primary rebuild of the deleted disk-first server
// enrichment (view-adapter.mjs, clinical-insights.mjs, care-structures.mjs).
//
// FAIL-SOFT CONTRACT (matches the priorContext fix in medplum-encounter.js):
// every leg is independently caught and defaults to the tolerated empty shape, so
// a degraded/forbidden query trims that one panel and the consult still renders.
// The whole function never throws.
//
// BOUNDED: runs on every consult open, so each query is _count-capped and the
// four legs run in parallel; the caller already has the patient's visit history
// (loadPatientHistoryFromMedplum) and threads it in for the "what changed" diff
// rather than re-querying encounters.
import { getMedplum } from './medplum-client.js';
import { loadPatientCare } from './care.js';
import {
  shapeLabObservation,
  buildMarkers,
  buildTrends,
  buildVitalSeries,
  buildWhatChanged,
  gatherInsightInputs,
  buildInsightsBar,
  buildInsightsRanked,
  buildInsightsBlock,
  buildEpisode,
} from './shape/consult-enrichment.js';

const EMPTY = Object.freeze({
  markers: [], vitalSeries: [], insights: null, insightsRanked: null,
  insightsBar: { changes: [], trends: [], inconsistencies: [] }, episode: null,
});

/**
 * Load + compute the longitudinal enrichment overlay for one consult.
 *
 * @param {object}   a
 * @param {string}   a.patientId    Medplum Patient UUID (the logged-in doctor's
 *                                  RLS grant already covers this compartment —
 *                                  the encounter graph was read the same way).
 * @param {string}   a.encounterId  the current encounter's Medplum UUID.
 * @param {Array}    a.visits       shapePatientHistory() rows (newest-first,
 *                                  INCLUDING the current visit) already fetched by
 *                                  the caller — reused for the "what changed" diff.
 * @returns {{ markers, vitalSeries, insights, insightsRanked, insightsBar, episode }}
 *          Always resolves (never throws); every field defaults to its empty shape.
 */
export async function enrichConsultView({ patientId, encounterId, visits = [] } = {}) {
  if (!patientId) return { ...EMPTY };
  let medplum;
  try { ({ medplum } = await getMedplum()); } catch { return { ...EMPTY }; }
  const ref = `Patient/${patientId}`;

  // Four independent, capped, parallel reads. Each fails soft to [] / null.
  const [labObsRes, vitalObsRes, careRes] = await Promise.all([
    // Discrete lab Observations across ALL the patient's encounters.
    medplum.searchResources('Observation', `patient=${ref}&category=laboratory&_count=500`).catch(() => []),
    // Vital-sign Observations across all encounters (BP panel + pulse/SpO2/…).
    medplum.searchResources('Observation', `patient=${ref}&category=vital-signs&_count=500`).catch(() => []),
    // CarePlan (Goal is read-forbidden for doctors — care.js surfaces goal names
    // from CarePlan.goal[].display; see buildEpisode's note).
    loadPatientCare(patientId).catch(() => null),
  ]);

  // Shape lab Observations into engine rows once; reused by markers/trends/diff.
  const labRows = (Array.isArray(labObsRes) ? labObsRes : []).map(shapeLabObservation).filter(Boolean);
  const vitalObs = Array.isArray(vitalObsRes) ? vitalObsRes : [];

  const markers = safe(() => buildMarkers(labRows), []);
  const vitalSeries = safe(() => buildVitalSeries(vitalObs), []);
  const trends = safe(() => buildTrends(labRows), []);
  const whatChanged = safe(() => buildWhatChanged({ visits, encounterId, labRows, vitalObs }), null);

  const inputs = gatherInsightInputs({ trends, whatChanged, inconsistencies: [], safety: [] });
  const insights = safe(() => buildInsightsBlock(inputs), null);
  const insightsRanked = safe(() => buildInsightsRanked(inputs), null);
  const insightsBar = safe(() => buildInsightsBar(inputs), { changes: [], trends: [], inconsistencies: [] });
  const episode = safe(() => buildEpisode(careRes), null);

  return { markers, vitalSeries, insights, insightsRanked, insightsBar, episode };
}

function safe(fn, fallback) { try { const v = fn(); return v === undefined ? fallback : v; } catch { return fallback; } }
